import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const supermetrics: Competitor = {
  "id": "supermetrics",
  "name": "Supermetrics",
  "group": "ecomm",
  "website": "https://supermetrics.com",
  "owner": "Independent, privately held (Helsinki, Finland; backed by Highland Europe, IVP and others). Acquired Relay42 (CDP) in June 2025.",
  "summary": "A marketing data pipeline that moves data from ad, analytics and ecommerce platforms into spreadsheets, Looker Studio, Power BI, AI chat tools and, on the Enterprise plan, cloud warehouses or Supermetrics' own managed Storage. It now markets itself as a Marketing Intelligence Platform with AI dashboards and an MCP server.",
  "bestFor": [
    "Marketing teams that want ad-platform and Shopify data in Google Sheets or Looker Studio without engineering help",
    "Brands that already have Snowflake or BigQuery and want a managed ELT feed of marketing sources instead of building API pipelines",
    "Agencies and in-house teams that report across many ad accounts and want AI chat access (Claude, ChatGPT, Gemini, Copilot) to that data"
  ],
  "notFor": [
    "Teams expecting a prebuilt profit or contribution-margin model; it moves data but does not model COGS, fees or refunds into profit",
    "Small brands that need warehouse delivery on a small budget, since warehouses and Storage require the custom-priced Enterprise plan",
    "Brands whose main operational sources are ERP or wholesale systems (no native NetSuite connector was found)"
  ],
  "strengths": [
    "Hundreds of maintained connectors (ads, analytics, ecommerce, email, CRM) replace hand-built API ingestion, which is usually the most fragile part of a custom stack",
    "Native connectors for Shopify, Amazon Seller Central, Recharge, and TikTok Shop (early access since Feb 2026)",
    "Can land data directly in Snowflake, BigQuery, Redshift, Databricks, Microsoft Fabric and others, so it can serve as the ingestion layer of a custom build rather than a replacement for it",
    "Supermetrics Storage offers a centrally managed data store for teams that do not want to run a warehouse",
    "Self-serve entry plans with published prices, starting at $44/month billed yearly",
    "AI features include natural-language dashboards (Studio), an MCP server, and official integrations with ChatGPT, Claude, Gemini and Copilot",
    "A low-code Connector Builder (by request) and Custom Data Import via SFTP, URL or email for sources it does not cover"
  ],
  "limitations": [
    "Warehouse destinations, Supermetrics Storage and data transformations are Enterprise-only (custom quote). Starter and Growth deliver to Sheets, Excel, Looker Studio, Power BI and AI chat",
    "The Shopify connector computes COGS from the product's current unit cost, not the cost at the time of sale, and treats an empty cost as 100% margin",
    "Shopify refunds are dated when processed, canceled refunds are not reported, and edited orders can create duplicate entries, so refund and edit logic still needs modeling downstream",
    "Shopify data is imported about twice a day, and Shopify's API can occasionally return incomplete order data that requires a re-index request to Shopify",
    "The Amazon Seller Central field list covers orders, sales and refunds but no fee or settlement fields, so marketplace fees must come from elsewhere",
    "Data arrives as standard denormalized tables per source. Matching orders across Shopify, Amazon and TikTok Shop, and building a unified customer or product model, is left to the customer",
    "Each Shopify store counts as a separate account against the plan's account limits"
  ],
  "pricing": {
    "model": "Tiered subscription priced by destinations, data sources, accounts per source and users; Enterprise is custom quoted",
    "publicStartingPrice": "$44/month billed yearly ($55 monthly) for Starter",
    "freeTier": "no - 14-day free trial, no credit card required",
    "notes": "Starter: $44/mo yearly or $55 monthly; 1 destination, 3 data sources, 3 accounts per source, 1 user, 4,000 AI credits/month. Growth: $177/mo yearly or $222 monthly; 1 destination, 6 sources, 7 accounts per source, 2 users, 12,000 AI credits; paid extras such as an extra destination (+$149/mo yearly) or user (+$99/mo yearly). Enterprise: custom quote; adds data warehousing (Snowflake, BigQuery, Redshift and others), Supermetrics Storage, transformations, Data and Platform API, custom data import, SAML SSO and data residency. Cost is driven by the number of destinations, data sources, ad or store accounts per source, and users, plus Enterprise for any warehouse delivery. Row volume is not listed as a price driver on the public page. In 2026 Starter rose from $37 to $44/mo (yearly) and per-unit Extras were added (per PricingSaaS, third-party). The vendor does not publish setup time.",
    "sourceUrl": "https://supermetrics.com/product/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native connector covering orders, returns, products, inventory and customers.",
      "sourceUrl": "https://supermetrics.com/connectors/shopify"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon Seller Central connector covering orders, sales and refunds. No fee or settlement fields are listed.",
      "sourceUrl": "https://docs.supermetrics.com/docs/amazon-seller-central-fields.md"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "TikTok Shop connector added in early access in February 2026.",
      "sourceUrl": "https://supermetrics.com/blog/february-2026-product-updates",
      "badge": "Early access"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "No native NetSuite or Faire connector was found. Shopware and Odoo CRM are in early access. Custom Data Import (SFTP, URL, email) and Connector Builder can fill gaps.",
      "sourceUrl": "https://supermetrics.com/product/snowflake"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google, TikTok Ads and many more.",
      "sourceUrl": "https://supermetrics.com/connectors/shopify"
    },
    "first_party_pixel_attribution": {
      "value": "no",
      "note": "It moves platform-reported data. No proprietary tracking pixel or multi-touch attribution model is offered in the pricing or product pages reviewed.",
      "sourceUrl": "https://supermetrics.com/product/pricing"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not found in the product pages reviewed.",
      "sourceUrl": "https://supermetrics.com/product/pricing"
    },
    "profit_cogs": {
      "value": "partial",
      "note": "The Shopify connector exposes gross profit and COGS fields, but these use the current unit cost. There is no contribution-margin model with shipping or fees.",
      "sourceUrl": "https://docs.supermetrics.com/docs/good-to-know-about-shopify.md"
    },
    "ltv_cohorts": {
      "value": "unknown",
      "note": "No prebuilt LTV or cohort reports were found; raw customer and order data is available for downstream modeling.",
      "sourceUrl": "https://supermetrics.com/connectors/shopify"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "A Recharge connector (31 metrics, 98 dimensions) delivers raw data. Prebuilt subscription analytics were not found.",
      "sourceUrl": "https://docs.supermetrics.com/docs/recharge.md"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Enterprise plan: Snowflake, BigQuery, Redshift, Databricks, Fabric and others, plus a managed Supermetrics Storage option.",
      "sourceUrl": "https://supermetrics.com/product/pricing",
      "badge": "Enterprise plan"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Custom fields and data blending, also exposed through the Public API since June 2026.",
      "sourceUrl": "https://supermetrics.com/blog/june-2026-updates"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "No in-product SQL editor was found. SQL access to Supermetrics Storage is not documented.",
      "sourceUrl": "https://docs.supermetrics.com/docs/supermetrics-storage.md"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Studio AI dashboards, an MCP server, and ChatGPT, Claude, Gemini and Copilot integrations. MCP requires a plan that includes the API.",
      "sourceUrl": "https://docs.supermetrics.com/docs/supermetrics-ai-features.md"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app was found.",
      "sourceUrl": "https://supermetrics.com/product/pricing"
    }
  },
  "recentChanges": [
    {
      "date": "2025-06",
      "change": "Acquired Relay42, a Dutch real-time customer data platform, to add audience activation.",
      "sourceUrl": "https://en.wikipedia.org/wiki/Supermetrics"
    },
    {
      "date": "2025-11",
      "change": "Launched Supermetrics AI: Dashboard, Insights and Connector agents, an MCP server and expanded Enterprise APIs.",
      "sourceUrl": "https://supermetrics.com/blog/supermetrics-ai-launch"
    },
    {
      "date": "2026-02",
      "change": "Data API opened to all plans. TikTok Shop, Shopware and Odoo CRM connectors entered early access. Databricks and Microsoft Fabric were added as warehouse destinations (early access). Official ChatGPT, Claude, Gemini and Copilot integrations launched.",
      "sourceUrl": "https://supermetrics.com/blog/february-2026-product-updates"
    },
    {
      "date": "2026-05",
      "change": "Starter price rose from $37 to $44/mo (yearly), and per-unit Extras were added for destinations, users, sources and accounts (third-party pricing tracker).",
      "sourceUrl": "https://pricingsaas.com/companies/supermetrics/diffs/2026Q2"
    },
    {
      "date": "2026-06",
      "change": "Added a ChatGPT Ads connector (early access). Custom fields and data blending became available through the Public API.",
      "sourceUrl": "https://supermetrics.com/blog/june-2026-updates"
    }
  ],
  "sources": [
    {
      "title": "Supermetrics pricing",
      "url": "https://supermetrics.com/product/pricing"
    },
    {
      "title": "Supermetrics Shopify connector",
      "url": "https://supermetrics.com/connectors/shopify"
    },
    {
      "title": "Supermetrics docs: Good to know about Shopify",
      "url": "https://docs.supermetrics.com/docs/good-to-know-about-shopify.md"
    },
    {
      "title": "Supermetrics docs: Amazon Seller Central fields",
      "url": "https://docs.supermetrics.com/docs/amazon-seller-central-fields.md"
    },
    {
      "title": "Supermetrics docs: Recharge",
      "url": "https://docs.supermetrics.com/docs/recharge.md"
    },
    {
      "title": "Supermetrics docs: Supermetrics Storage",
      "url": "https://docs.supermetrics.com/docs/supermetrics-storage.md"
    },
    {
      "title": "Supermetrics docs: Data warehouses and cloud storage",
      "url": "https://docs.supermetrics.com/docs/data-warehouses-and-cloud-storage"
    },
    {
      "title": "Supermetrics for Snowflake",
      "url": "https://supermetrics.com/product/snowflake"
    },
    {
      "title": "Supermetrics docs: AI features",
      "url": "https://docs.supermetrics.com/docs/supermetrics-ai-features.md"
    },
    {
      "title": "Supermetrics AI launch (Nov 2025)",
      "url": "https://supermetrics.com/blog/supermetrics-ai-launch"
    },
    {
      "title": "February 2026 product updates",
      "url": "https://supermetrics.com/blog/february-2026-product-updates"
    },
    {
      "title": "June 2026 product updates",
      "url": "https://supermetrics.com/blog/june-2026-updates"
    },
    {
      "title": "Wikipedia: Supermetrics",
      "url": "https://en.wikipedia.org/wiki/Supermetrics"
    },
    {
      "title": "Irish Tech News: Supermetrics acquires Relay42",
      "url": "https://irishtechnews.ie/supermetrics-acquires-relay42/amp/"
    },
    {
      "title": "PricingSaaS: Supermetrics 2026 Q2 pricing changes (third-party)",
      "url": "https://pricingsaas.com/companies/supermetrics/diffs/2026Q2"
    }
  ],
  "lastReviewed": "2026-11-03"
};
